
import { supabase } from '@/integrations/supabase/client';

// Use explicit types to avoid TypeScript recursion issues
export interface Profile {
  id: string;
  email?: string | null;
  full_name?: string | null;
  username?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  job_title?: string | null;
  company?: string | null;
  whatsapp_number?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateProfileData {
  full_name?: string;
  email?: string;
  username?: string;
  whatsapp_number?: string;
}

export interface UpdateProfileData {
  username?: string;
  full_name?: string;
  avatar_url?: string;
  bio?: string;
  job_title?: string;
  company?: string;
  whatsapp_number?: string;
}

export class UserProfileRepository {
  private userCache = new Map<string, { profile: Profile; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  async findByEmail(email: string): Promise<Profile | null> {
    try {
      console.log('🔍 Buscando usuário por email:', email);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }

      console.log('✅ Usuário encontrado por email');
      return data as Profile;
    } catch (error) {
      console.error('❌ Erro ao buscar usuário por email:', error);
      return null;
    }
  }

  async findByUserIdAndId(userId: string, id: string): Promise<Profile | null> {
    // Check cache first
    const cached = this.userCache.get(userId);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.profile;
    }

    try {
      console.log('🔍 Buscando perfil do usuário:', userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }
      
      const profile = data as Profile;
      
      // Cache the result
      this.userCache.set(userId, { profile, timestamp: Date.now() });
      
      console.log('✅ Perfil encontrado e cacheado');
      return profile;
    } catch (error) {
      console.error('❌ Erro ao buscar perfil:', error);
      return null;
    }
  }

  async createProfile(userId: string, data: CreateProfileData): Promise<Profile> {
    try {
      console.log('➕ Criando perfil para usuário:', userId);
      
      const profileData = {
        id: userId,
        ...data,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: profile, error } = await supabase
        .from('profiles')
        .insert(profileData)
        .select()
        .single();

      if (error) throw error;
      
      const newProfile = profile as Profile;
      
      // Cache the new profile
      this.userCache.set(userId, { profile: newProfile, timestamp: Date.now() });
      
      console.log('✅ Perfil criado com sucesso');
      return newProfile;
    } catch (error) {
      console.error('❌ Erro ao criar perfil:', error);
      throw error;
    }
  }

  async updateProfile(userId: string, data: UpdateProfileData): Promise<Profile> {
    try {
      console.log('📝 Atualizando perfil do usuário:', userId);
      
      const updateData = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const { data: profile, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      
      const updatedProfile = profile as Profile;
      
      // Update cache
      this.userCache.set(userId, { profile: updatedProfile, timestamp: Date.now() });
      
      console.log('✅ Perfil atualizado com sucesso');
      return updatedProfile;
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil:', error);
      throw error;
    }
  }

  async deleteProfile(userId: string): Promise<boolean> {
    try {
      console.log('🗑️ Removendo perfil do usuário:', userId);
      
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      // Clear cache
      this.userCache.delete(userId);
      
      console.log('✅ Perfil removido com sucesso');
      return true;
    } catch (error) {
      console.error('❌ Erro ao remover perfil:', error);
      return false;
    }
  }

  clearUserCache(userId: string): void {
    this.userCache.delete(userId);
    console.log('🧹 Cache do usuário limpo:', userId);
  }

  async refreshUserData(userId: string): Promise<Profile | null> {
    this.clearUserCache(userId);
    return this.findByUserIdAndId(userId, userId);
  }
}
